import { AddedTopBottomLocations } from './../../features/service-insights/types/service-insight-types';
import { Injectable } from "@angular/core";
import { FiltersService } from "./filters.service";
import { TVDService } from "src/app/features/total-value-delivery/services/tvd.service";
import { TVDDistributorService } from "src/app/features/total-value-delivery/services/tvd-distributor.service";
import { ContractModel } from "src/app/global/contract-filter-model";
import { availableBrandGroups, ModifiedFormData, TvdPptModel } from "src/app/features/total-value-delivery/types/tvd-types";
import { TVDModel } from "src/app/features/total-value-delivery/types/tvd-model";
import { AddedServiceMetric, ServiceInsightsPresentationData } from 'src/app/features/service-insights/types/service-insight-types';
import { ServiceInsightsService } from "src/app/features/service-insights/services/service-insights-service.service";
import { HttpClientService } from './http-client.service';
import { CurrencyUomService } from './currency-uom.service';
import { Division, Region } from './ebr-api-client/ebr-api-client.g';
import { TemplateTranslationsService } from './template-translations.service';
import { PresentationType } from '../types/presentation-types';

//////////////////////////// PPT 2.0 ////////////////////////////
@Injectable({
  providedIn: 'root'
})
export class PresentationService {
  private filters: ContractModel | undefined;

  constructor(private _filtersService: FiltersService,
    private _httpClientService: HttpClientService,
    private _tvdService: TVDService,
    private _tvdDistService: TVDDistributorService,
    private serviceInsightsService: ServiceInsightsService,
    private _currencyUomService: CurrencyUomService,
    private _templateTranslationsService: TemplateTranslationsService
  ) {}




  // Division/single-region defaults, checked in order; region defaults require every selected region to match.
  private static readonly DIVISION_PRESENTATION_DEFAULTS: [Division, PresentationType][] = [
    [Division.QSR, PresentationType.QSR],
    [Division.FRS, PresentationType.FRS],
    [Division.Pest, PresentationType.PestTVD],
    [Division.Distributor, PresentationType.Distributor],
  ];

  private static readonly SINGLE_REGION_PRESENTATION_DEFAULTS: [Region, PresentationType][] = [
    [Region.NA, PresentationType.TvdNA],
    [Region.EU, PresentationType.TvdEU],
    [Region.AP, PresentationType.TvdAP],
    [Region.LA, PresentationType.TvdLA],
    [Region.IMEA, PresentationType.TvdIMEA],
  ];

  // Falls back to defaultType only when the user hasn't already selected a presentation type.
  private resolvePresentationTypeCode(defaultType: PresentationType): string {
    return (this.filters!.presentationType == undefined || this.filters!.presentationType.length == 0)
      ? defaultType
      : this.filters!.presentationType;
  }

  /**
   * Gets the selected presentation code based on selected filters.
   * For single region scenarios, the user selected template in the UI is converted to its corresponding code.
   * If multiple regions are selected, then the user has to select a presentation template code mandatorily in the UI
   */
  getSelectedPresentationTypeCode() : string{
    this._filtersService.selectedFiltersObservable.subscribe(
      (f) => (this.filters = f)
    );

    if (!this.filters) {
      return '';
    }

    const divisionDefault = PresentationService.DIVISION_PRESENTATION_DEFAULTS
      .find(([division]) => this.filters!.division == division)?.[1];
    if (divisionDefault) {
      return this.resolvePresentationTypeCode(divisionDefault);
    }

    const regionDefault = PresentationService.SINGLE_REGION_PRESENTATION_DEFAULTS
      .find(([region]) => this.filters!.regions?.every(r => r == region))?.[1];
    if (regionDefault) {
      return this.resolvePresentationTypeCode(regionDefault);
    }

    if (this.filters.regions && this.filters.regions.length > 1) {
      // For multi-region scenario the template code selection is mandatory
      return this.filters.presentationType!;
    }

    return this.resolvePresentationTypeCode(PresentationType.Other);
  }
}

export type PresentationPayload = {
  contractModel: ContractModel | undefined,
  tvdModel: TvdPptModel | undefined,
  addedFindings: ServiceInsightsPresentationData | undefined,
  selectedMetrics: AddedServiceMetric[] | undefined,
  addedTopBottomLocs: AddedTopBottomLocations | undefined,
  costAssumptionsMetrics: ModifiedFormData | undefined,
  brandGroup?: availableBrandGroups,
  brands?: string[],
  currencyCode: string,
  unitOfMeasure: string,
}
